import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';

import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { useDeleteCommand } from '$tablet-app/src/app/chat/hooks/useDeleteCommand';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import { Text } from '@akin-home/text/native';
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from '@akin-home/toast/native';
import React, { useCallback, useEffect, useState } from 'react';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import {
  useDeleteRobotIdentity,
  useUpdateRobotIdentity,
} from '../hooks/mutations';
import { useGetRobotIdentity } from '../hooks/queries';
import { useEditRobotFaceEmbeddings } from '../hooks/useEditRobotFaceEmbeddings';
import { RobotIdentity } from '../types';
import { RobotIdentityFormScreenView } from './RobotIdentityFormScreenView';

const context = {
  canSave: true,
  canDelete: true,
  fields: {
    name: { type: 'text' },
    notes: { type: 'text' },
  },
};

const parameters = {
  contact_id: 'text',
};

export const EditRobotIdentityScreen = (
  props: RootStackScreenProps<'EditRobotIdentity'>
) => {
  const {
    route: {
      params: { robotId, identity_id },
    },
    navigation: { goBack },
  } = props;

  const robot = useRobotStateStore((state) => state.robotDevices[robotId]);

  if (!robot) {
    showErrorToast({
      text1: 'Robot not found.',
      text2: 'Please try again later.',
    });
    goBack();
  }

  useUpdateScreenChatContext(context);

  const { data: fetchedIdentity } = useGetRobotIdentity(robot, identity_id);
  const [identity, setIdentity] = useState<Partial<RobotIdentity>>(
    fetchedIdentity ?? {}
  );

  useEffect(() => {
    if (fetchedIdentity && !identity) {
      setIdentity(fetchedIdentity);
    }
  }, [identity, fetchedIdentity]);

  const updateMutation = useUpdateRobotIdentity(robot);
  const deleteMutation = useDeleteRobotIdentity(robot);

  const onSubmit = useSaveCommand(
    useCallback(async () => {
      if (updateMutation.isLoading || !identity || !identity_id) return;

      try {
        if (!identity.name) {
          showErrorToast({
            text1: 'Name of Identity cannot be blank.',
          });
          return;
        }

        showInfoToast({ text1: 'Saving Identity...' });

        await updateMutation.mutateAsync({
          identity_id,
          identityUpdates: identity,
        });
        showSuccessToast({
          text1: 'Identity Saved',
        });
        goBack();
      } catch (err) {
        console.error('Error when submitting update identity:', err);
        showErrorToast({
          text1: 'Error in Saving Identity',
          text2: 'Please try again.',
        });
      }
    }, [updateMutation, identity, identity_id, goBack])
  );

  const onDelete = useDeleteCommand(
    useCallback(async () => {
      try {
        if (!identity_id) {
          showErrorToast({
            text1: 'Identity cannot be found.',
          });
          return;
        }

        showInfoToast({ text1: 'Deleting Identity...' });

        await deleteMutation.mutateAsync(identity_id);
        showSuccessToast({
          text1: 'Identity Deleted',
        });
        goBack();
      } catch (err) {
        console.error('Error when deleting identity:', err);
        showErrorToast({
          text1: 'Error in Deleting Identity',
          text2: 'Please try again.',
        });
      }
    }, [identity_id, deleteMutation, goBack])
  );

  const {
    isLoading: isFaceEmbeddingsLoading,
    onChangeFaceEmbeddings,
    onDeleteFaceEmbeddings,
  } = useEditRobotFaceEmbeddings(robot, {
    createOptions: {
      onSuccess: () => setIdentity({ ...identity, has_face_embeddings: true }),
    },
    updateOptions: {
      onSuccess: () => setIdentity({ ...identity, has_face_embeddings: true }),
    },
    deleteOptions: {
      onSuccess: () => setIdentity({ ...identity, has_face_embeddings: false }),
    },
  });

  if (!identity) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <RobotIdentityFormScreenView
        screenType={'edit'}
        identity={identity}
        onChange={setIdentity}
        onCancel={goBack}
        isSubmitting={updateMutation.isLoading}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isEditingFaceEmbeddings={isFaceEmbeddingsLoading}
        onChangeFaceEmbeddings={() => onChangeFaceEmbeddings(identity_id)}
        onDeleteFaceEmbeddings={() => onDeleteFaceEmbeddings(identity_id)}
      />
      <DeveloperScreenInfo
        context={<Text>{JSON.stringify(context, null, 3)}</Text>}
        parameters={<Text>{JSON.stringify(parameters, null, 3)}</Text>}
      />
    </>
  );
};
